import debounce from 'lodash/debounce'
import { applyPrices, applyMainPointUpdate, concernsResource, sortMainPoints } from '@/util/novaLive'

export default {
  data: () => ({
    pollingListener: null,
    currentlyPolling: false,
    liveBound: false,
    liveRetryTimer: null,
    flashedIds: {},
  }),

  /**
   * Unbind the polling listener before the component is destroyed.
   */
  beforeUnmount() {
    this.stopPolling()
  },

  created() {
    this.debouncedLiveRefresh = debounce(() => {
      if (this.currentlyPolling) {
        this.getResources({ silent: true })
      }
    }, 400)

    this.debouncedWindowSync = debounce(() => {
      if (this.currentlyPolling) {
        this.getResources({ silent: true })
      }
    }, 1500)

    // Keep index metric cards in sync with Pusher / polling without
    // hammering on every price tick (status / create / delete only).
    this.debouncedMetricRefresh = debounce(() => {
      if (!this.currentlyPolling || !this.resourceName) {
        return
      }

      Nova.$emit('metric-refresh', { resourceName: this.resourceName })
    }, 700)
  },

  methods: {
    initializePolling() {
      this.currentlyPolling =
        this.currentlyPolling || this.resourceResponse.polling

      if (this.currentlyPolling && !this.liveBound && this.pollingListener === null) {
        this.startPolling()
      }
    },

    /**
     * Toggle polling for new resources.
     */
    togglePolling() {
      if (this.currentlyPolling) {
        this.stopPolling()
      } else {
        this.startPolling()
      }
    },

    /**
     * Pause live updates (socket or interval).
     */
    stopPolling() {
      this.debouncedLiveRefresh?.cancel?.()
      this.debouncedWindowSync?.cancel?.()
      this.debouncedMetricRefresh?.cancel?.()
      this.stopIntervalPolling()
      this.detachLive()
      this.currentlyPolling = false
    },

    /**
     * Start live updates: Pusher when configured, interval as fallback.
     */
    startPolling() {
      this.currentlyPolling = true

      if (this.attachLive()) {
        this.stopIntervalPolling()
        return
      }

      this.startIntervalPolling()
      this.scheduleLiveRetry()
    },

    startIntervalPolling() {
      if (this.pollingListener !== null) return

      this.pollingListener = setInterval(() => {
        this.refreshIfIdle()
      }, this.pollingInterval)
    },

    stopIntervalPolling() {
      if (this.pollingListener) {
        clearInterval(this.pollingListener)
        this.pollingListener = null
      }
    },

    attachLive() {
      if (this.liveBound) return true
      if (typeof Nova.$on !== 'function') return false
      if (!Nova.config('pusher')?.key) return false
      if (typeof Nova.echo === 'function' && !Nova.echo()) return false

      this.onLiveUpdate = this.onLiveUpdate.bind(this)
      Nova.$on('nova-live', this.onLiveUpdate)
      this.liveBound = true

      return true
    },

    detachLive() {
      if (this.liveRetryTimer) {
        clearTimeout(this.liveRetryTimer)
        this.liveRetryTimer = null
      }

      if (this.liveBound && this.onLiveUpdate) {
        Nova.$off('nova-live', this.onLiveUpdate)
      }

      this.liveBound = false
    },

    scheduleLiveRetry() {
      if (this.liveRetryTimer || this.liveBound) return
      if (!Nova.config('pusher')?.key) return

      this.liveRetryTimer = setTimeout(() => {
        this.liveRetryTimer = null
        if (!this.currentlyPolling || this.liveBound) return

        if (this.attachLive()) {
          this.stopIntervalPolling()
        } else {
          this.scheduleLiveRetry()
        }
      }, 800)
    },

    /**
     * Restart polling for the resource.
     */
    restartPolling() {
      if (this.currentlyPolling === true) {
        this.stopPolling()
        this.startPolling()
      }
    },

    refreshIfIdle() {
      const selectedResources = this.selectedResources ?? []

      if (
        document.hasFocus() &&
        document.querySelectorAll('[data-modal-open]').length < 1 &&
        selectedResources.length < 1
      ) {
        this.getResources({ silent: true })
        this.debouncedMetricRefresh()
      }
    },

    onLiveUpdate(data) {
      if (!this.currentlyPolling) return
      if (!concernsResource(data, this.resourceName)) return
      if (document.querySelectorAll('[data-modal-open]').length > 0) return
      if ((this.selectedResources ?? []).length > 0) return

      if (
        this.viaResource === 'main-points' &&
        this.viaResourceId &&
        data.payload?.main_point_id != null &&
        String(data.payload.main_point_id) !== String(this.viaResourceId)
      ) {
        return
      }

      if (data.action === 'prices') {
        this.applyLivePrices(data.payload?.prices || {})
        return
      }

      if (
        (data.action === 'updated' || data.action === 'created') &&
        this.resourceName === 'main-points' &&
        data.id != null
      ) {
        this.applyLiveMainPoint(data.id, data.payload || {})
      }

      this.debouncedLiveRefresh()
      this.debouncedMetricRefresh()
    },

    applyLiveMainPoint(id, payload) {
      if (!this.resources?.length) return

      const row = this.resources.find(resource => String(resource.id?.value) === String(id))
      if (!row) return

      if (!applyMainPointUpdate(row, payload)) return

      if (!this.currentOrderBy) {
        this.resources = sortMainPoints(this.resources)
      } else {
        this.resources = [...this.resources]
      }

      this.flashRows([String(id)])
    },

    applyLivePrices(prices) {
      if (!prices || !this.resources?.length) return

      const changedIds = []

      for (const resource of this.resources) {
        if (applyPrices(resource, this.resourceName, prices)) {
          changedIds.push(String(resource.id.value))
        }
      }

      if (!changedIds.length) return

      if (this.resourceName === 'main-points' && !this.currentOrderBy) {
        this.resources = sortMainPoints(this.resources)
        this.debouncedWindowSync()
      } else {
        this.resources = [...this.resources]
      }

      this.flashRows(changedIds)
    },

    flashRows(ids) {
      const next = { ...this.flashedIds }
      for (const id of ids) {
        next[id] = Date.now()
      }
      this.flashedIds = next

      setTimeout(() => {
        const keep = { ...this.flashedIds }
        const cutoff = Date.now() - 700
        let dirty = false
        for (const id of Object.keys(keep)) {
          if (keep[id] <= cutoff) {
            delete keep[id]
            dirty = true
          }
        }
        if (dirty) this.flashedIds = keep
      }, 800)
    },
  },

  computed: {
    initiallyPolling() {
      return this.resourceResponse.polling
    },

    pollingInterval() {
      return this.resourceResponse.pollingInterval
    },

    /**
     * Determine if the polling toggle button should be shown.
     */
    shouldShowPollingToggle() {
      if (!this.resourceResponse) return false

      return this.resourceResponse.showPollingToggle || false
    },
  },
}
